import React from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatIDR, formatDate } from "@/lib/format";

export default function ReportsPage() {
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = new Date(new Date().setDate(1)).toISOString().slice(0, 10);
  const [start, setStart] = React.useState(monthStart);
  const [end, setEnd] = React.useState(today);
  const [rev, setRev] = React.useState(null);
  const [util, setUtil] = React.useState(null);
  const [outs, setOuts] = React.useState([]);
  const [byCat, setByCat] = React.useState([]);
  const [canc, setCanc] = React.useState(null);

  const load = async () => {
    const [r, u, o, cat, canc] = await Promise.all([
      api.get("/reports/revenue", { params: { start, end } }),
      api.get("/reports/utilization"),
      api.get("/reports/outstanding"),
      api.get("/reports/by-category"),
      api.get("/reports/cancellation-rate"),
    ]);
    setRev(r.data); setUtil(u.data); setOuts(o.data);
    setByCat(cat.data); setCanc(canc.data);
  };

  React.useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  return (
    <div className="space-y-4" data-testid="reports-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>Laporan</h1>
        <p className="text-sm text-neutral-500">Laporan bisnis dengan filter tanggal.</p>
      </div>

      <div className="card-flat p-4">
        <div className="flex items-end gap-3 flex-wrap">
          <div><Label>Dari</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="report-start" /></div>
          <div><Label>Sampai</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="report-end" /></div>
          <Button onClick={load} className="bg-green-600 hover:bg-green-700" data-testid="report-apply">Terapkan</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card-flat p-4">
          <div className="text-xs uppercase text-neutral-500">Pendapatan Periode</div>
          <div className="text-2xl font-bold mt-2 text-green-700">{formatIDR(rev?.total || 0)}</div>
          <div className="text-xs text-neutral-500">{rev?.count || 0} transaksi</div>
        </div>
        <div className="card-flat p-4">
          <div className="text-xs uppercase text-neutral-500">Utilisasi Equipment</div>
          <div className="text-2xl font-bold mt-2">{util?.utilization || 0}%</div>
          <div className="text-xs text-neutral-500">{util?.rented || 0} disewa · {util?.available || 0} tersedia · total {util?.total || 0}</div>
        </div>
        <div className="card-flat p-4">
          <div className="text-xs uppercase text-neutral-500">Outstanding Invoice</div>
          <div className="text-2xl font-bold mt-2 text-red-700">{outs.length}</div>
          <div className="text-xs text-neutral-500">{formatIDR(outs.reduce((s, i) => s + (i.outstanding || 0), 0))} total</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="card-flat p-4">
          <div className="text-sm font-semibold mb-3">Pendapatan per Kategori</div>
          <div className="space-y-2">
            {byCat.map((c, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span>{c.category}</span>
                <span className="font-medium text-green-700">{formatIDR(c.value)}</span>
              </div>
            ))}
            {byCat.length === 0 && <div className="text-xs text-neutral-500">Belum ada data.</div>}
          </div>
        </div>
        <div className="card-flat p-4">
          <div className="text-sm font-semibold mb-3">Cancellation Rate</div>
          <div className="text-3xl font-bold text-red-700">{canc?.rate || 0}%</div>
          <div className="text-xs text-neutral-500 mt-1">{canc?.cancelled || 0} dari {canc?.total || 0} booking dibatalkan</div>
        </div>
      </div>

      <div className="card-flat p-4">
        <div className="flex justify-between items-center mb-3">
          <div className="text-sm font-semibold">Detail Pembayaran</div>
          <Button variant="outline" size="sm" onClick={() => {
            const rows = ["Tanggal,Invoice,Metode,Jumlah"];
            (rev?.payments || []).forEach((p) => rows.push(`${formatDate(p.date)},${p.invoice_id},${p.method},${p.amount}`));
            const csv = rows.join("\n");
            const blob = new Blob([csv], { type: "text/csv" });
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob); a.download = `pembayaran-${start}-${end}.csv`; a.click();
          }} data-testid="report-export-csv">Ekspor CSV</Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full data-table">
            <thead><tr><th className="text-left">Tanggal</th><th className="text-left">Metode</th><th className="text-left">Ref</th><th className="text-right">Jumlah</th></tr></thead>
            <tbody>
              {(rev?.payments || []).map((p) => (
                <tr key={p.id}>
                  <td className="text-xs">{formatDate(p.date)}</td>
                  <td className="text-xs capitalize">{p.method}</td>
                  <td className="text-xs">{p.reference || "-"}</td>
                  <td className="text-right">{formatIDR(p.amount)}</td>
                </tr>
              ))}
              {(!rev?.payments || rev.payments.length === 0) && <tr><td colSpan={4} className="text-center py-6 text-sm text-neutral-500">Tidak ada data.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
